'use client';

import { useState, useEffect, useCallback } from 'react';
import { useCorporate } from '@/context/CorporateContext';

const BIOMETRIC_KEY = 'tenant_biometric_payments_enabled';

export const useBiometrics = () => {
    const { config } = useCorporate();
    const [isSupported, setIsSupported] = useState<boolean>(false);
    const [isEnabled, setIsEnabled] = useState<boolean>(true);

    useEffect(() => {
        const checkSupport = async () => {
            if (typeof window !== 'undefined' && window.PublicKeyCredential) {
                const available = await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
                setIsSupported(available);
            } else {
                setIsSupported(false);
            }
        };

        // Load preference from localStorage
        const savedPreference = localStorage.getItem(BIOMETRIC_KEY);
        if (savedPreference !== null) {
            setIsEnabled(savedPreference === 'true');
        } else {
            // Default to true as per user request
            localStorage.setItem(BIOMETRIC_KEY, 'true');
            setIsEnabled(true);
        }

        checkSupport();
    }, []);

    const toggleBiometrics = (value: boolean) => {
        setIsEnabled(value);
        localStorage.setItem(BIOMETRIC_KEY, value.toString());
    };

    const verifyUser = useCallback(async (): Promise<boolean> => {
        console.log(`[Biometrics] verifyUser called. isSupported=${isSupported}, isEnabled=${isEnabled}`);

        if (!isSupported || !isEnabled) {
            console.log("[Biometrics] Skipping WebAuthn. Falling back to PIN.");
            return false;
        }

        try {
            // We use an empty "get" or "create" standard challenge to trigger the native biometric prompt.
            // For a pure local check, this is the most reliable cross-platform way on browsers.
            const challenge = new Uint8Array(32);
            window.crypto.getRandomValues(challenge);

            const options: CredentialCreationOptions = {
                publicKey: {
                    challenge,
                    rp: { name: `${config?.name || 'Platform'} Wallet` },
                    user: {
                        id: new Uint8Array(16),
                        name: `user@${config?.name?.toLowerCase().replace(/\s/g, '') || 'platform'}`,
                        displayName: `${config?.name || 'Platform'} User`
                    },
                    pubKeyCredParams: [{ alg: -7, type: "public-key" }],
                    authenticatorSelection: {
                        // Use preferred to avoid instant failures if hardware is not fully set up
                        userVerification: "preferred",
                        // Omit platform attachment to allow security keys/phones if platform is unavailable
                        // authenticatorAttachment: "platform" 
                    },
                    timeout: 60000
                }
            };

            console.log("[Biometrics] Triggering navigator.credentials.create...");
            await navigator.credentials.create(options);
            console.log("[Biometrics] navigator.credentials.create succeeded.");
            return true;
        } catch (error: any) {
            console.error("[Biometrics] Verification failed or cancelled:", error.name, error.message);
            return false;
        }
    }, [isSupported, isEnabled, config]);

    return {
        isSupported,
        isEnabled,
        verifyUser,
        toggleBiometrics
    };
};
